// Centralized in-memory mock data. localStorage-backed where useful.
import { fetchWithAuth } from '../utils/api';
import { clearBillTemplates } from './billTemplateStore';

let listeners = [];
export const subscribe = (listener) => {
  listeners.push(listener);
  return () => { listeners = listeners.filter((l) => l !== listener); };
};
export const notify = () => listeners.forEach((l) => l());
let loading = false;
export const isStoreLoading = () => loading;
export const setStoreLoading = (state) => { loading = state; notify(); };

export const DEFAULT_THEME = Object.freeze({
  primary: '#0a5132',
  secondary: '#faf6ec',
  tertiary: '#efe9d8',
});

export const ORG_TYPES = Object.freeze({
  PRODUCT: 'product',
  SERVICE: 'service',
  BOTH: 'both',
});

export const ROLES = Object.freeze({
  SUPER_ADMIN: 'super-admin',
  ORG_ADMIN: 'org-admin',
  BRANCH_ADMIN: 'branch-admin',
  WAREHOUSE_ADMIN: 'warehouse-admin',
  WAREHOUSE_USER: 'warehouse-user',
  ORG_USER: 'org-user',
});

// ---- Master Data (Read Only) ----
export const masterStates = [];
export const masterDistricts = [];

// ---- Organizations ----
// The `modules` flag map mirrors the Super Admin's per-org module toggles.
// Zone-aware features (zone / branches / warehouse) gate their UI off these
// flags so an org sees only what its tenancy entitles it to.
export const organizations = [];

// Whether a module is enabled for an org. Defaults to false when the modules
// map is missing — safer than defaulting to true (a stale localStorage blob
// without a modules key would otherwise unlock every tab).
export const isModuleOn = (org, moduleId) => {
  if (!org) return false;
  
  if (moduleId === 'pos' || moduleId === 'catalog') return true;
  
  // Only feedback is forced as a core module for service/both orgs
  const isServiceOrBoth = org.type === ORG_TYPES.SERVICE || org.type === ORG_TYPES.BOTH;
  if (isServiceOrBoth && moduleId === 'feedback') return true;

  if (!org.modules) {
    if (moduleId === 'printer' || moduleId === 'scanner') return false;
    return true; // Default to true if modules map is missing (legacy orgs / loading phase)
  }
  return !!org.modules[moduleId];
};

// ---- Plan / subscription helpers ----
// Duration options offered to org-admins on the Resubscribe page. Must mirror
// the SuperAdmin's list — the backend recognises these four ids.
export const DURATION_OPTIONS = [
  { id: 'week',   label: '1 Week',   tagline: 'Free trial',  months: 0,  free: true },
  { id: 'month',  label: '1 Month',  tagline: 'Monthly',     months: 1 },
  { id: '6month', label: '6 Months', tagline: 'Half-yearly', months: 6 },
  { id: 'year',   label: '1 Year',   tagline: 'Yearly',      months: 12 },
];

// Whether the org's plan window has expired. Returns false when no plan window
// is set (e.g. pre-existing orgs from before pricing existed) so they aren't
// accidentally locked out.
export const isPlanExpired = (org) => {
  if (!org || !org.planExpiresAt) return false;
  const expiry = new Date(org.planExpiresAt);
  if (Number.isNaN(expiry.getTime())) return false;
  return expiry.getTime() < Date.now();
};

// Days remaining until expiry. Negative when already expired. Null when no plan.
export const daysUntilExpiry = (org) => {
  if (!org || !org.planExpiresAt) return null;
  const expiry = new Date(org.planExpiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  const ms = expiry.getTime() - Date.now();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
};

// Compute monthly base for an org from its modules + pricing config. Matches
// the SuperAdmin's logic exactly so the renewal price equals what was charged
// at creation (assuming the SA hasn't changed pricing in the meantime).
export const monthlyBaseForOrg = ({ pricing, orgType, modules }) => {
  if (!pricing || !orgType) return 0;
  const perMod = pricing.manualPrices?.[orgType] || {};
  // Mirror the SuperAdmin MODULES list — keep in sync if modules change there.
  const KNOWN = ['pos', 'catalog', 'inventory', 'purchase', 'zone', 'branches',
    'warehouse', 'users', 'reports', 'delivery', 'transport', 'ecommerce'];
  const CORE = new Set(['pos', 'catalog']);
  return KNOWN.reduce((sum, id) => {
    const isOn = CORE.has(id) || (modules && modules[id]);
    return isOn ? sum + Number(perMod[id] || 0) : sum;
  }, 0);
};

export const priceForDuration = (monthlyBase, duration) => {
  const opt = DURATION_OPTIONS.find((d) => d.id === duration);
  if (!opt || opt.free) return 0;
  return Number(monthlyBase || 0) * opt.months;
};

// ---- Users ----
// Super Admin uses the platform. org-admin spans all branches of an org;
// branch-admin and org-user belong to a single branch (branchId).
export const users = [];

// ---- Sales (bills) ----
// Each bill stores per-line GST split (CGST + SGST for local sales).
export const sales = [];
export const stockHistory = [];
export const units = [];
export const customerFeedback = [];
export const appointments = [];
export const memberships = [];
export const customerMemberships = [];

export const membershipsOf = (orgId) => memberships.filter(m => m.orgId === orgId).reverse();
export const customerMembershipsOf = (orgId) => customerMemberships.filter(m => m.orgId === orgId).reverse();
export const appointmentsOf = (orgId) => appointments.filter(a => a.orgId === orgId).reverse();

// ---- Helpers ----
export const nextLocalId = (prefix) => `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;

export const generateNextOrgId = () => {
  let maxNum = 1000;
  organizations.forEach((org) => {
    if (org.id && org.id.startsWith('org-')) {
      const num = parseInt(org.id.substring(4), 10);
      if (!isNaN(num) && num > maxNum) {
        maxNum = num;
      }
    }
  });
  return `org-${maxNum + 1}`;
};

export const findOrgById = (id) => organizations.find((o) => o.id === id) || null;
export const findUserByCredentials = (email, password, orgId = null) => {
  const lowered = (email || '').trim().toLowerCase();
  return users.find((u) =>
    u.email.toLowerCase() === lowered &&
    u.password === password &&
    (orgId ? u.orgId === orgId : true)) || null;
};
export const findSuperAdminByCredentials = (email, password) => {
  const u = findUserByCredentials(email, password);
  return u && u.role === ROLES.SUPER_ADMIN ? u : null;
};
export const orgUsersOf = (orgId) => users.filter((u) => u.orgId === orgId);
export const branchUsersOf = (branchId) => users.filter((u) => u.branchId === branchId);

export const replaceOrganization = (id, patch) => {
  const idx = organizations.findIndex((o) => o.id === id);
  if (idx >= 0) organizations[idx] = { ...organizations[idx], ...patch };
  fetchWithAuth(`/organizations/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
};
export const addOrganization = (record) => {
  const id = record.id || generateNextOrgId();
  const orgObj = { ...record, id };
  organizations.push(orgObj);
  fetchWithAuth('/organizations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orgObj),
  });
  return id;
};
export const removeOrganization = (id) => {
  const idx = organizations.findIndex((o) => o.id === id);
  if (idx >= 0) organizations.splice(idx, 1);
  fetchWithAuth(`/organizations/${id}`, { method: 'DELETE' });
};

// ---- Org-scoped selectors ----
export const salesOf = (orgId, userId = null) => sales.filter((s) => s.orgId === orgId && (userId ? s.userId === userId : true)).reverse();

const replaceIn = (arr, id, patch) => {
  const idx = arr.findIndex((r) => r.id === id);
  if (idx >= 0) arr[idx] = { ...arr[idx], ...patch };
};
const removeFrom = (arr, id) => {
  const idx = arr.findIndex((r) => r.id === id);
  if (idx >= 0) arr.splice(idx, 1);
};

// ---- Store reset ----
// Call this whenever switching tenants so stale data from the previous org
// never bleeds into the newly selected org's views.
export const clearAllStoreData = () => {
  // The bill designs belong to the shop being left, so they go with it.
  clearBillTemplates();
  // So does how it prices. Carrying one shop's setting into the next would
  // put a tax-inclusive total in front of a shop that charges on top.
  orgTaxMode = 'exclusive';
  users.length = 0;
  zones.length = 0;
  warehouses.length = 0;
  branches.length = 0;
  gstRates.length = 0;
  pCategories.length = 0;
  pSubCategories.length = 0;
  sCategories.length = 0;
  sSubCategories.length = 0;
  catalogProducts.length = 0;
  catalogServices.length = 0;
  stockRows.length = 0;
  purchaseOrders.length = 0;
  whPurchases.length = 0;
  whPurchaseOrders.length = 0;
  sales.length = 0;
  stockTransfers.length = 0;
  emergencyRequests.length = 0;
  deliveryAgents.length = 0;
  deliveries.length = 0;
  storefrontSettings.length = 0;
  storeListings.length = 0;
  onlineOrders.length = 0;
  suppliers.length = 0;
  deliveryFees.length = 0;
  customerFeedback.length = 0;
  appointments.length = 0;
  memberships.length = 0;
  customerMemberships.length = 0;
  notify();
};

let isDataLoading = false;
let lastLoadedOrgId = null;
let currentTenantLoadPromise = null;

const applyStoreData = (key, data) => {
  let arr;
  if (key === 'users') arr = users;
  else if (key === 'zones') arr = zones;
  else if (key === 'warehouses') arr = warehouses;
  else if (key === 'branches') arr = branches;
  else if (key === 'gstRates') arr = gstRates;
  else if (key === 'pCategories') arr = pCategories;
  else if (key === 'pSubCategories') arr = pSubCategories;
  else if (key === 'sCategories') arr = sCategories;
  else if (key === 'sSubCategories') arr = sSubCategories;
  else if (key === 'catalogProducts') arr = catalogProducts;
  else if (key === 'catalogServices') arr = catalogServices;
  else if (key === 'units') arr = units;
  else if (key === 'shifts') arr = shifts;
  else if (key === 'stockRows') arr = stockRows;
  else if (key === 'stockHistory') arr = stockHistory;
  else if (key === 'purchaseOrders') arr = purchaseOrders;
  else if (key === 'whPurchases') arr = whPurchases;
  else if (key === 'whPurchaseOrders') arr = whPurchaseOrders;
  else if (key === 'sales') arr = sales;
  else if (key === 'stockTransfers') arr = stockTransfers;
  else if (key === 'emergencyRequests') arr = emergencyRequests;
  else if (key === 'deliveryAgents') arr = deliveryAgents;
  else if (key === 'deliveries') arr = deliveries;
  else if (key === 'orgTax') {
    setShopTaxModeLocal(data?.taxMode);
    return;
  }
  else if (key === 'storefrontSettings') {
    storefrontSettings.length = 0;
    if (data && !Array.isArray(data)) {
      storefrontSettings.push(data);
    } else if (Array.isArray(data)) {
      storefrontSettings.push(...data);
    }
    return;
  }
  else if (key === 'storeListings') arr = storeListings;
  else if (key === 'onlineOrders') arr = onlineOrders;
  else if (key === 'states') arr = masterStates;
  else if (key === 'districts') arr = masterDistricts;
  else if (key === 'suppliers') arr = suppliers;
  else if (key === 'deliveryFees') arr = deliveryFees;
  else if (key === 'customerFeedback') arr = customerFeedback;
  else if (key === 'appointments') arr = appointments;
  else if (key === 'memberships') arr = memberships;
  else if (key === 'customerMemberships') arr = customerMemberships;
  else if (key === 'organizations') {
    organizations.length = 0;
    if (Array.isArray(data)) {
      const mapped = data.map((org) => {
        let modulesObj = {};
        try {
          if (typeof org.modules === 'string') {
            modulesObj = JSON.parse(org.modules);
          } else if (org.modules && typeof org.modules === 'object') {
            modulesObj = org.modules;
          }
        } catch (e) {
          console.error("Failed to parse modules:", e);
        }
        return {
          ...org,
          theme: {
            primary: org.themePrimary || '#0a5132',
            secondary: org.themeSecondary || '#faf6ec',
            tertiary: org.themeTertiary || '#efe9d8',
          },
          modules: modulesObj,
          barcodeMode: org.barcodeMode || 'both',
        };
      });
      organizations.push(...mapped);
    }
    return;
  }

  if (arr && Array.isArray(data)) {
    arr.length = 0;
    if (key === 'stockRows') {
      arr.push(...data.filter(r => r.note !== 'T0 Stock'));
    } else {
      arr.push(...data);
    }
  }
};

export const loadAllTenantData = async (org, background = false) => {
  const orgId = typeof org === 'string' ? org : org?.id;
  if (!orgId) return;

  const record = (typeof org === 'string' ? findOrgById(org) : org) || {};
  const orgType = record.type;
  const isProduct = orgType === ORG_TYPES.PRODUCT || orgType === ORG_TYPES.BOTH;
  const isService = orgType === ORG_TYPES.SERVICE || orgType === ORG_TYPES.BOTH;
  const modules = record.modules || {};

  // If already loading the same org, share the in-flight promise to avoid duplicate requests
  if (isDataLoading && lastLoadedOrgId === orgId && currentTenantLoadPromise) {
    return currentTenantLoadPromise;
  }
  // If switching orgs, cancel any in-flight load and wipe stale data immediately.
  if (lastLoadedOrgId !== orgId) {
    isDataLoading = false;
    currentTenantLoadPromise = null;
    clearAllStoreData();
  }
  isDataLoading = true;
  lastLoadedOrgId = orgId;

  // Only show blocking spinner if there is no core branch data yet
  const hasExistingData = branches.length > 0;
  if (!background && !hasExistingData) {
    setStoreLoading(true);
  }

  currentTenantLoadPromise = (async () => {
    try {
      const fetchEndpoint = async ([key, path]) => {
        try {
          const res = await fetchWithAuth(path, {
            headers: { 'x-org-id': orgId }
          });
          if (res.ok) {
            const data = await res.json();
            return { key, data };
          }
        } catch (e) {
          console.error(`Failed to fetch ${key}:`, e);
        }
        return { key, data: [] };
      };

      // PHASE 1: Core operational endpoints needed to display the UI immediately
      const coreEndpoints = {
        users: '/users',
        zones: '/zones',
        branches: '/branches',
        organizations: '/organizations',
        states: '/states',
        districts: '/districts',
        gstRates: '/gst-rates',
        suppliers: '/suppliers',
        units: '/org/units',
        orgTax: '/org-settings/tax',
      };

      if (isProduct) {
        coreEndpoints.warehouses = '/warehouses';
        coreEndpoints.pCategories = '/product-categories';
        coreEndpoints.pSubCategories = '/product-subcategories';
        coreEndpoints.catalogProducts = '/products';
        coreEndpoints.stockRows = '/stock';
      }

      if (isService) {
        coreEndpoints.sCategories = '/service-categories';
        coreEndpoints.sSubCategories = '/service-subcategories';
        coreEndpoints.catalogServices = '/services';
      }

      // Execute Phase 1
      const coreResults = await Promise.all(Object.entries(coreEndpoints).map(fetchEndpoint));
      coreResults.forEach(({ key, data }) => applyStoreData(key, data));
      notify();

      // UI is ready! Unblock spinner immediately
      if (!background) setStoreLoading(false);

      // PHASE 2: Heavy historical and secondary module endpoints in background
      const deferredEndpoints = {
        sales: `/billing/history?orgId=${orgId}`,
        shifts: '/staff-shifts',
      };

      if (isProduct) {
        deferredEndpoints.purchaseOrders = '/purchase-orders';
        deferredEndpoints.whPurchases = '/wh-purchases';
        deferredEndpoints.whPurchaseOrders = '/wh-purchase-orders';
        deferredEndpoints.stockHistory = '/stock-history';
        deferredEndpoints.stockTransfers = '/stock-transfers';
      }

      if (isService) {
        deferredEndpoints.appointments = '/appointments';
        deferredEndpoints.memberships = '/membershipplans';
        deferredEndpoints.customerMemberships = '/customermemberships';
      }

      if (modules.delivery) {
        deferredEndpoints.deliveryAgents = '/delivery-agents';
        deferredEndpoints.deliveries = '/deliveries';
        deferredEndpoints.deliveryFees = '/delivery-fees';
      }

      if (modules.ecommerce) {
        deferredEndpoints.storefrontSettings = '/storefront-settings';
        deferredEndpoints.storeListings = '/store-listings';
        deferredEndpoints.onlineOrders = '/online-orders';
      }

      if (modules.feedback) {
        deferredEndpoints.customerFeedback = '/feedback';
      }

      if (modules.emergency) {
        deferredEndpoints.emergencyRequests = '/emergency-requests';
      }

      // Execute Phase 2 quietly in background
      const deferredResults = await Promise.all(Object.entries(deferredEndpoints).map(fetchEndpoint));
      deferredResults.forEach(({ key, data }) => applyStoreData(key, data));
      notify();

    } catch (e) {
      console.error('Failed to fetch org data', e);
    } finally {
      isDataLoading = false;
      currentTenantLoadPromise = null;
      if (!background) setStoreLoading(false);
    }
  })();

  return currentTenantLoadPromise;
};

export const refreshDeliveries = async () => {
  try {
    const res = await fetchWithAuth('/deliveries');
    if (res.ok) {
      const data = await res.json();
      deliveries.length = 0;
      deliveries.push(...data);
      notify();
    }
  } catch(e) { console.error('refreshDeliveries failed', e); }
};

export const refreshTransport = async () => {
  try {
    const res1 = await fetchWithAuth('/stock-transfers');
    if (res1.ok) {
      const data = await res1.json();
      stockTransfers.length = 0;
      stockTransfers.push(...data);
    }
    const res2 = await fetchWithAuth('/emergency-requests');
    if (res2.ok) {
      const data = await res2.json();
      emergencyRequests.length = 0;
      emergencyRequests.push(...data);
    }
    notify();
  } catch(e) { console.error('refreshTransport failed', e); }
};

// ---- Users CRUD ----
export const initialsOf = (name) => (name || '')
  .trim()
  .split(/\s+/)
  .map((w) => w[0])
  .join('')
  .slice(0, 2)
  .toUpperCase() || 'U';

/**
 * What the server said about a save, in a form a screen can act on.
 *
 * These three functions draw the change on screen before the server is asked,
 * which is right - a staff list that waits half a second to redraw feels broken.
 * What was wrong is that they then ignored the answer. A refusal left the row
 * sitting in the list, the screen said "Staff member added", and the account did
 * not exist. It came back the next time the page was reloaded, by which point
 * the person had written the password down and gone.
 *
 * So every one of them now undoes its own optimistic change when the server
 * refuses, and hands the reason back. Nothing throws: four of the six callers
 * do not await these, and turning a refused save into an unhandled rejection
 * would trade a silent wrong screen for a silent console error.
 */
const saveResult = async (res) => {
  if (res && res.ok) return { ok: true };
  let said = '';
  try {
    const body = await res.json();
    said = body.error || body.message || '';
  } catch { /* no JSON body - the status is all there is */ }
  return { ok: false, error: said || 'The server refused to save that. Nothing was changed.' };
};

export const addUser = async (record) => {
  const id = record.id || nextLocalId('u');
  const initials = record.initials || initialsOf(record.name);
  const userObj = { ...record, id, initials };
  users.push(userObj);
  notify();

  // Include the generated id in the backend payload — sending id:'' would
  // either collide on the PK constraint or leak a row with an empty primary key.
  const payload = { ...record, id, initials };
  // The password the form collected. There is no fallback any more: it used to
  // be the word "admin", which meant every staff account saved without one -
  // and until today the form had no box to type one in, so that was all of them
  // - could be signed into by anybody who guessed five letters.
  //
  // The form requires it now, so an empty value here is a caller that did not
  // ask. Sent empty, the server sets a password nothing can match and the only
  // way in is Forgot Password, which is the safe end to fail at.
  payload.PasswordHash = record.password || '';

  let res;
  try {
    res = await fetchWithAuth('/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    removeFrom(users, id);
    notify();
    return { ok: false, error: 'Cannot reach the server. Nothing was saved.' };
  }

  const out = await saveResult(res);
  if (!out.ok) {
    // The row went on screen before the server was asked. It has to come back
    // off, or the list is advertising an account nobody can sign in to - which
    // is how a refused duplicate e-mail or mobile number looked like success.
    removeFrom(users, id);
    notify();
    return out;
  }

  const serverUser = await res.json();
  userObj.id = serverUser.id;
  // Re-sync the full users list from the server so the optimistic entry is
  // replaced with authoritative data.
  try {
    const syncRes = await fetchWithAuth('/users');
    if (syncRes.ok) {
      const data = await syncRes.json();
      users.length = 0;
      users.push(...data);
      notify();
    }
  } catch { /* ignore - the saved entry is still in the array */ }
  return { ok: true };
};
export const updateUser = async (id, patch) => {
  const next = { ...patch };
  if (patch.name) next.initials = initialsOf(patch.name);
  // The whole row as it stands, so a refusal can put it back. A patch tells you
  // what is changing but not what it is changing from.
  const was = users.find((u) => u.id === id);
  const before = was ? { ...was } : null;
  replaceIn(users, id, next);
  notify();

  let res;
  try {
    res = await fetchWithAuth(`/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(next),
    });
  } catch {
    if (before) { replaceIn(users, id, before); notify(); }
    return { ok: false, error: 'Cannot reach the server. Nothing was changed.' };
  }

  const out = await saveResult(res);
  if (!out.ok) {
    if (before) { replaceIn(users, id, before); notify(); }
    return out;
  }
  return { ok: true };
};
export const deleteUser = async (id) => {
  const was = users.find((u) => u.id === id);
  const before = was ? { ...was } : null;
  removeFrom(users, id);
  notify();

  let res;
  try {
    res = await fetchWithAuth(`/users/${id}`, { method: 'DELETE' });
  } catch {
    if (before) { users.push(before); notify(); }
    return { ok: false, error: 'Cannot reach the server. Nothing was deleted.' };
  }

  const out = await saveResult(res);
  if (!out.ok) {
    // Put them back. A staff member who looks deleted and is not can still sign
    // in to the till, which is the worst way for this to be wrong.
    if (before) { users.push(before); notify(); }
    return out;
  }
  return { ok: true };
};

// ---- Sales (bill creation) ----
// Builds a bill with per-line GST split (local sale -> CGST + SGST), records it,
// and decrements active stock at the selling location.
export const createBill = ({
  orgId, locationId, userId, customer, customerPhone, method, orderType, deliveryAddress,
  deliveryState, items,
}) => {
  const lines = items.map((it) => {
    const gst = findGst(it.gstId);
    const rate = gst ? gst.rate : 0;
    // What this line bills under. The server settles it again from its own
    // records and the bill is whatever the server says; this is here so the
    // receipt printed from this side says the same thing.
    const taxMode = taxModeOf(findCatalogProduct(it.productId) || it, orgTaxMode);
    const split = splitGst(rate, false);
    const gross = +(it.qty * it.price).toFixed(2);
    const cut = taxOf(gross, rate, isInclusive(taxMode));
    const base = +cut.base.toFixed(2);
    // Halved from the line's tax, not worked out twice from the base: two
    // roundings of a half can leave a paisa belonging to neither column.
    const cgst = split.igst ? 0 : +(cut.tax / 2).toFixed(2);
    const sgst = split.igst ? 0 : +(cut.tax - cgst).toFixed(2);
    const igst = split.igst ? +cut.tax.toFixed(2) : 0;
    const lineTax = +(cgst + sgst + igst).toFixed(2);
    return {
      productId: it.productId,
      variantId: it.variantId || '',
      name: it.name,
      label: it.label || '',
      categoryId: it.categoryId || it.category || '',
      batchNumber: it.batchNumber || '',
      expiryDate: it.expiryDate || '',
      staffId: it.staffId || null,
      isService: !!it.isService,
      qty: it.qty,
      price: it.price,
      gstRate: rate,
      taxMode,
      base,
      cgst,
      sgst,
      igst,
      lineTax,
      lineTotal: +(base + lineTax).toFixed(2),
    };
  });

  const sum = (k) => +lines.reduce((s, l) => s + l[k], 0).toFixed(2);
  const subtotal = sum('base');
  const totalCgst = sum('cgst');
  const totalSgst = sum('sgst');
  const totalIgst = sum('igst');
  const totalTax = +(totalCgst + totalSgst + totalIgst).toFixed(2);
  // What the goods and the tax come to, and then what is actually taken.
  // The server rounds the same way and the bill is whatever the server says;
  // this is here so the receipt printed from this side says the same thing.
  const exact = +(subtotal + totalTax).toFixed(2);
  const roundOff = roundOffOf(exact);
  const total = payable(exact);
  const tempId = `bill-temp-${Date.now()}`;
  const billObj = {
    id: tempId,
    orgId,
    locationId,
    userId,
    customer: customer || 'Walk-in',
    phone: customerPhone || '',
    method,
    orderType: orderType || 'in-shop',
    deliveryAddress: orderType === 'set-to-deliver' ? (deliveryAddress || '') : '',
    date: new Date().toISOString().slice(0, 10),
    // The clock, kept separately from the date: kitchen tickets and token slips
    // are useless without it, and `date` is a day with no time in it.
    createdAt: new Date().toISOString(),
    lines,
    subtotal,
    totalCgst,
    totalSgst,
    totalIgst,
    totalTax,
    roundOff,
    total,
    items: lines.length,
  };
  sales.push(billObj);
  items.forEach((it) => {
    let row = null;
    if (it.stockRowId) {
      row = stockRows.find((r) => r.id === it.stockRowId && r.status === 'active');
    }
    if (!row) {
      row = stockRows.find((r) => r.orgId === orgId && r.locationId === locationId
        && r.productId === it.productId && r.variantId === (it.variantId || '') && r.status === 'active');
    }
    if (row) row.qty = Math.max(0, row.qty - it.qty);

    // Keep catalog product / variant stockQty in sync for T0 lightweight tracking
    const cp = catalogProducts.find((p) => p.id === it.productId && p.orgId === orgId);
    if (cp) {
      if (it.variantId) {
        const v = (cp.variants || []).find((x) => x.id === it.variantId);
        if (v && typeof v.stockQty === 'number') v.stockQty = Math.max(0, v.stockQty - it.qty);
      } else if (typeof cp.stockQty === 'number') {
        cp.stockQty = Math.max(0, cp.stockQty - it.qty);
      }
    }
  });

  const payload = {
    orgId,
    locationId,
    customer: customer || 'Walk-in',
    method,
    orderType: orderType || 'in-shop',
    deliveryAddress: orderType === 'set-to-deliver' ? (deliveryAddress || '') : '',
    // Where a delivered order is going. A fact about the order, like the
    // address above it - whether it makes the sale inter-state is the
    // server's decision, from this and the branch's own state.
    //
    // isIgst used to be sent, hardcoded false, which is why no bill this
    // system ever wrote had a paisa of IGST on it. The server ignores the
    // field now and works it out.
    deliveryState: orderType === 'set-to-deliver' ? (deliveryState || '') : '',
    phone: customerPhone || '',
    items: items.map(it => ({
      productId: it.productId,
      serviceId: it.isService ? it.productId : '',
      staffId: it.staffId || '',
      isService: !!it.isService,
      variantId: it.variantId || '',
      name: it.name,
      label: it.label || '',
      price: Number(it.price) || 0,
      qty: Number(it.qty) || 0,
      gstId: it.gstId
    }))
  };

  // The real bill number is issued by the server. Until this settles, `id` is
  // the local `bill-temp-…` placeholder — which used to be what was printed on
  // the customer's receipt, because printing started the instant this function
  // returned. Callers that put a bill in front of a customer await `ready`
  // first; it resolves true once the sale is recorded and the number is real,
  // and false if the server refused it, so a rejected sale prints nothing.
  const ready = fetchWithAuth('/billing/checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  }).then(async (res) => {
    if (res.ok) {
      const data = await res.json();
      if (data && data.bill) {
        billObj.id = data.bill.id;
        notify(); // trigger re-render to remove #temp tag
      }
      if (orderType === 'set-to-deliver') {
        addDelivery({
          // Share the bill's id. SalesLedgerTab looks the delivery up by the
          // bill id (both for the Delivery Status filter and for the Status
          // column), and the API keeps whatever id is posted. Without this the
          // generated 'dl-*' id could never match, so the status filter matched
          // nothing and emptied the table.
          id: billObj.id,
          orgId,
          locationId,
          customer: customer || 'Walk-in',
          phone: customerPhone || '',
          address: deliveryAddress || '',
          lines,
          subtotal,
          fee: 0,
          total,
          method,
          status: 'new',
          createdAt: new Date().toISOString().slice(0, 10),
        });
      }
      return true;
    } else {
      let errStr = "Checkout failed";
      try {
        const errorData = await res.json();
        errStr = errorData.error || errStr;
      } catch (e) {}
      console.error(errStr);
      // Remove the optimistic bill from sales array so it doesn't vanish silently on refresh later
      const idx = sales.findIndex(s => s.id === tempId);
      if (idx >= 0) {
        sales.splice(idx, 1);
        notify();
      }
      alert(`Sale failed to record: ${errStr}`);
      return false;
    }
  }).catch((err) => {
    console.error("Network error during checkout", err);
    const idx = sales.findIndex(s => s.id === tempId);
    if (idx >= 0) {
      sales.splice(idx, 1);
      notify();
    }
    alert("Network error: Sale failed to record.");
    return false;
  });

  // Non-enumerable so the promise never lands in a JSON payload or a snapshot
  // of the sales list; it is a handle for the caller, not part of the bill.
  Object.defineProperty(billObj, 'ready', { value: ready, enumerable: false });

  return billObj;
};

export const findUserById = (id) => users.find((u) => u.id === id) || null;

// ---- Zones ----
// Zones group branches and warehouses by region inside an organization.
// Branches and warehouses always live inside exactly one zone of their org.
export const zones = [];

// ---- Warehouses ----
// Each warehouse belongs to a zone. Every warehouse must have exactly one
// wh_purchase record (auto-created in addWarehouse; cascade-deleted with it).
export const warehouses = [];

// ---- Warehouse purchases (1:1 with warehouse, mandatory) ----
// One summary record per warehouse — the wh_purchase configuration. Actual
// line items (incoming purchase orders) live in `whPurchaseOrders` below.
export const whPurchases = [];

// Warehouse purchase orders — actual line items bringing stock into a
// warehouse. The Warehouse Admin manages these; they don't feed per-branch
// stockRows directly (that flow goes through warehouse → branch sourcing).
export const whPurchaseOrders = [];

// ---- Branches ----
// Each branch belongs to a zone, owns exactly one inventory, and pulls its
// inventory from `warehouseSourceIds` (any number, all in the same zone).
// `purchaseEnabled` is the per-branch opt-in flag for Inventory_Purchase —
// when true, the branch tracks its own purchases on top of warehouse-sourced
// stock; when false, only warehouse stock flows in.
export const branches = [];

export const branchesOf = (orgId) => branches.filter((b) => b.orgId === orgId);
export const mainBranchOf = (orgId) => branches.find((b) => b.orgId === orgId && b.isMain) || null;
export const findBranch = (id) => branches.find((b) => b.id === id) || null;

export const addBranch = (record) => {
  const id = record.id || nextLocalId('br');
  const branchObj = {
    ...record,
    id,
    isMain: false,
    warehouseSourceIds: record.warehouseSourceIds || [],
    purchaseEnabled: record.purchaseEnabled !== undefined ? !!record.purchaseEnabled : true,
  };
  branches.push(branchObj);

  fetchWithAuth('/branches', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(branchObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverBranch => {
        branchObj.id = serverBranch.id;
      });
    }
  });
  return id;
};
export const updateBranch = (id, patch) => {
  const existing = branches.find((b) => b.id === id);
  if (!existing) return;
  const { isMain, ...rest } = patch;
  replaceIn(branches, id, rest);
  // Without this, nothing subscribed to the store re-rendered after a branch
  // was edited — so choosing a bill design for a branch left the old one on
  // screen until the page was reloaded.
  notify();

  fetchWithAuth(`/branches/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rest),
  });
};
export const deleteBranch = (id) => {
  const b = branches.find((x) => x.id === id);
  if (!b || b.isMain) return false;
  removeFrom(branches, id);

  fetchWithAuth(`/branches/${id}`, {
    method: 'DELETE',
  });
  return true;
};
export const setBranchPurchaseEnabled = (id, enabled) => {
  replaceIn(branches, id, { purchaseEnabled: !!enabled });
  const b = branches.find(x => x.id === id);
  if (b) {
    fetchWithAuth(`/branches/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(b),
    });
  }
};

// ---- Zones CRUD + selectors ----
export const zonesOf = (orgId) => zones.filter((z) => z.orgId === orgId);
export const findZone = (id) => zones.find((z) => z.id === id) || null;
export const branchesOfZone = (zoneId) => branches.filter((b) => b.zoneId === zoneId);
export const warehousesOfZone = (zoneId) => warehouses.filter((w) => w.zoneId === zoneId);

export const addZone = (record) => {
  const id = record.id || nextLocalId('zn');
  const zoneObj = { ...record, id };
  zones.push(zoneObj);

  fetchWithAuth('/zones', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(zoneObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverZone => {
        zoneObj.id = serverZone.id;
      });
    }
  });
  return id;
};
export const updateZone = (id, patch) => {
  replaceIn(zones, id, patch);
  fetchWithAuth(`/zones/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
};
export const deleteZone = (id) => {
  if (branchesOfZone(id).length || warehousesOfZone(id).length) return false;
  removeFrom(zones, id);
  fetchWithAuth(`/zones/${id}`, {
    method: 'DELETE',
  });
  return true;
};

// ---- Warehouses CRUD + selectors ----
export const warehousesOf = (orgId) => warehouses.filter((w) => w.orgId === orgId);
export const findWarehouse = (id) => warehouses.find((w) => w.id === id) || null;
export const findWhPurchase = (warehouseId) =>
  whPurchases.find((p) => p.warehouseId === warehouseId) || null;
export const whPurchasesOf = (orgId) => whPurchases.filter((p) => p.orgId === orgId).reverse();

export const addWarehouse = (record) => {
  const id = record.id || nextLocalId('wh');
  const whObj = { ...record, id };
  warehouses.push(whObj);

  const whpObj = {
    id: nextLocalId('whp'),
    warehouseId: id,
    orgId: record.orgId,
    lastDate: null,
    totalOrders: 0,
    status: 'Active',
  };
  whPurchases.push(whpObj);

  fetchWithAuth('/warehouses', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(whObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverWh => {
        whObj.id = serverWh.id;
        whpObj.warehouseId = serverWh.id;
      });
    }
  });
  return id;
};
export const updateWarehouse = (id, patch) => {
  replaceIn(warehouses, id, patch);
  fetchWithAuth(`/warehouses/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
};
export const deleteWarehouse = (id) => {
  for (let i = whPurchases.length - 1; i >= 0; i -= 1) {
    if (whPurchases[i].warehouseId === id) whPurchases.splice(i, 1);
  }
  branches.forEach((b) => {
    if (Array.isArray(b.warehouseSourceIds) && b.warehouseSourceIds.includes(id)) {
      b.warehouseSourceIds = b.warehouseSourceIds.filter((wid) => wid !== id);
    }
  });
  removeFrom(warehouses, id);

  fetchWithAuth(`/warehouses/${id}`, {
    method: 'DELETE',
  });
};
export const updateWhPurchase = (id, patch) => {
  replaceIn(whPurchases, id, patch);
  fetchWithAuth(`/wh-purchases/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
};

// Branches that pull stock from this warehouse.
export const branchesSourcingWarehouse = (warehouseId) =>
  branches.filter((b) => Array.isArray(b.warehouseSourceIds) && b.warehouseSourceIds.includes(warehouseId));

// Warehouse Admin → their warehouse (or null if unscoped / no record).
export const warehouseOfUser = (user) => (user && user.warehouseId ? findWarehouse(user.warehouseId) : null);

// ---- Warehouse purchase orders CRUD ----
export const whPurchaseOrdersOf = (warehouseId) =>
  whPurchaseOrders.filter((p) => p.warehouseId === warehouseId);
export const findWhPurchaseOrder = (id) => whPurchaseOrders.find((p) => p.id === id) || null;

// Adding a purchase order bumps the wh_purchase summary so the parent
// warehouse card stays in sync (last date + order count).
export const addWhPurchaseOrder = (record) => {
  const id = record.id || nextLocalId('whpo');
  const po = { ...record, id };
  whPurchaseOrders.push(po);
  const summary = whPurchases.find((p) => p.warehouseId === po.warehouseId);
  if (summary) {
    summary.totalOrders = (summary.totalOrders || 0) + 1;
    if (!summary.lastDate || po.date > summary.lastDate) summary.lastDate = po.date;
    if (po.supplier && !summary.supplier) summary.supplier = po.supplier;
  }

  fetchWithAuth('/wh-purchase-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(po),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverPo => {
        po.id = serverPo.id;
        refreshStock();
      });
    }
  });
  return id;
};
export const updateWhPurchaseOrder = (id, patch) => {
  replaceIn(whPurchaseOrders, id, patch);
  fetchWithAuth(`/wh-purchase-orders/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(res => {
    if (res.ok) refreshStock();
  });
};
export const deleteWhPurchaseOrder = (id) => {
  const po = findWhPurchaseOrder(id);
  removeFrom(whPurchaseOrders, id);
  if (po) {
    const summary = whPurchases.find((p) => p.warehouseId === po.warehouseId);
    if (summary && summary.totalOrders > 0) summary.totalOrders -= 1;
  }

  fetchWithAuth(`/wh-purchase-orders/${id}`, {
    method: 'DELETE',
  });
};

// ---- Org Settings ----
// Zone-aware tenancy. The `modules` map on the organization (set by Super
// Admin) is the source of truth — these flags are mirrored here for the
// settings UI. `warehouseMode` is retired: warehouses are now first-class
// entities scoped to zones; purchases run per-warehouse (mandatory) and
// per-branch (opt-in via branch.purchaseEnabled).
export const orgSettings = [];

export const getOrgSettings = (orgId, orgObj = null) => {
  const org = findOrgById(orgId) || orgObj;
  let s = orgSettings.find((x) => x.orgId === orgId);
  if (!s) {
    s = { orgId };
    orgSettings.push(s);
  }
  // Mirror the module map so existing callers reading branchesEnabled /
  // warehouseEnabled stay correct without knowing about modules directly.
  // If org is still null (e.g. during initial load with no cached org), default to true to prevent flicker.
  s.zoneEnabled = org ? !!(!org.modules || org.modules.zone) : true;
  s.branchesEnabled = org ? !!(!org.modules || org.modules.branches) : true;
  s.warehouseEnabled = org ? !!(!org.modules || org.modules.warehouse) : true;
  return s;
};

// Branches the app should actually expose: all when enabled, else only Main.
export const effectiveBranchesOf = (orgId) => {
  const all = branchesOf(orgId);
  if (getOrgSettings(orgId).branchesEnabled) return all;
  const main = all.find((b) => b.isMain);
  return main ? [main] : all.slice(0, 1);
};
export const updateOrgSettings = (orgId, patch) => {
  const s = getOrgSettings(orgId);
  Object.assign(s, patch);
  return s;
};

// ---- Master: Suppliers / Vendors ----
// Each supplier is org-scoped; all branches share the same supplier list.
// Fields: supplierName, contactPerson, mobileNumber, email, gstNumber, address,
//         paymentTerms ('Immediate'|'Net 30'|'Net 60'), status ('Active'|'Inactive')
export const suppliers = [];

export const suppliersOf = (orgId) => suppliers.filter((s) => s.orgId === orgId).reverse();
export const activeSuppliersOf = (orgId) => suppliers.filter((s) => s.orgId === orgId && s.status !== 'Inactive').reverse();
export const findSupplier = (id) => suppliers.find((s) => s.id === id) || null;

export const addSupplier = (record) => {
  const id = record.id || nextLocalId('sup');
  const obj = { ...record, id };
  suppliers.push(obj);
  fetchWithAuth('/suppliers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(obj),
  }).then((res) => {
    if (res.ok) res.json().then((s) => { obj.id = s.id; });
  });
  return id;
};
export const updateSupplier = (id, patch) => {
  replaceIn(suppliers, id, patch);
  notify();
  fetchWithAuth(`/suppliers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
};
export const deleteSupplier = (id) => {
  removeFrom(suppliers, id);
  fetchWithAuth(`/suppliers/${id}`, { method: 'DELETE' });
};

// ---- Master: GST rates ----
// Each record is { name, rate% }. Bills split rate into CGST + SGST (rate/2 each)
// for local sales; IGST (full rate) is reserved for future inter-state selling.
export const gstRates = [];

// ---- Master: Product hierarchy — P-Category > P-Sub-Category > Product ----
export const pCategories = [];
export const pSubCategories = [];
export const sCategories = [];
export const sSubCategories = [];

// ---- GST CRUD ----
export const gstRatesOf = (orgId) => gstRates.filter((g) => g.orgId === orgId).reverse();
export const findGst = (id) => gstRates.find((g) => g.id === id) || null;
export const addGst = (record) => {
  const id = record.id || nextLocalId('gst');
  const gstObj = { ...record, id };
  gstRates.push(gstObj);

  fetchWithAuth('/gst-rates', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-org-id': record.orgId || '',
    },
    body: JSON.stringify(gstObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverGst => {
        gstObj.id = serverGst.id;
      });
    }
  });
  return id;
};
export const updateGst = (id, patch) => {
  const existing = findGst(id);
  const orgId = patch.orgId || (existing && existing.orgId) || '';
  replaceIn(gstRates, id, patch);
  fetchWithAuth(`/gst-rates/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'x-org-id': orgId,
    },
    body: JSON.stringify(patch),
  });
};
export const deleteGst = (id) => {
  const existing = findGst(id);
  const orgId = (existing && existing.orgId) || '';
  removeFrom(gstRates, id);
  fetchWithAuth(`/gst-rates/${id}`, {
    method: 'DELETE',
    headers: {
      'x-org-id': orgId,
    },
  });
};
// Splits a GST rate into bill components. Local sale -> CGST + SGST; otherwise IGST.
export const splitGst = (rate, interState = false) => (
  interState
    ? { cgst: 0, sgst: 0, igst: rate || 0 }
    : { cgst: (rate || 0) / 2, sgst: (rate || 0) / 2, igst: 0 }
);

// ---- Does a price already contain its GST? ----
//
// The same rule the server applies in checkout_pricing.go. The server totals
// every bill, so getting this wrong here does not lose the shop money - it
// shows a customer one number and charges them another, which is worse.

/**
 * Whether a mode means the price contains its tax.
 *
 * One-sided on purpose: only the word turns it on. A blank column on a row
 * saved before this setting existed, a typo, a value from a newer server -
 * all read as exclusive, which is what every bill in this system was before
 * there was a choice.
 */
export const isInclusive = (mode) => String(mode || '').trim().toLowerCase() === 'inclusive';

/** The mode a line bills under: the item's own if it states one, else sub-category's, else the shop's. */
export const taxModeOf = (item, shopMode) => {
  const direct = String(item?.taxMode || '').trim();
  if (direct) return direct;
  const subId = item?.subCategoryId || item?.subCategory?.id;
  if (subId) {
    const sub = findPSubCategory(subId) || findSSubCategory(subId);
    const subMode = String(sub?.taxMode || '').trim();
    if (subMode) return subMode;
  }
  return String(shopMode || '').trim();
};

const paise = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Split one line into the shop's share and the tax man's.
 *
 * Exclusive reads the gross as the price before tax and adds the rate on top.
 * Inclusive reads it as what the customer pays, tax already inside: an MRP of
 * 100 at 18% is 84.75 of goods and 15.25 of tax, not 118.
 *
 * The inclusive tax is gross - base, not base * rate / 100. The same number
 * in algebra, not always in floating point, and only this one guarantees
 * base + tax lands exactly on the printed price.
 */
export const taxOf = (gross, rate, inclusive) => {
  const r = Number(rate) || 0;
  const g = Number(gross) || 0;
  if (!inclusive) {
    const tax = paise((g * r) / 100);
    return { base: g, tax };
  }
  const tax = paise(g - (g * 100) / (100 + r));
  const base = paise(g - tax);
  return { base, tax };
};

/**
 * The whole rupee a customer actually hands over, and the paise it moved.
 *
 * A bill coming to 143.80 is charged 144; one coming to 143.20 is charged 143.
 * Nearest rupee - under fifty paise dropped, fifty or more raised - which is
 * what section 170 of the CGST Act says about an amount payable and what every
 * till in the country already does. Nobody has twenty paise to give.
 *
 * Nearest rather than always up, and the difference is not academic. Always up
 * keeps eighty paise on every bill ending in .20: nothing on one sale, and on
 * two hundred sales a day it is money taken with no line on the bill saying so.
 *
 * The tax is never touched. Rounding GST to make a total come out whole puts a
 * shop a few paise out with the department on every sale of the year, so the
 * paise are carried openly instead, on their own Round off line.
 *
 * The same rule the server applies in billing.go and the till applies in
 * mobile/src/tax.js. Three copies of one sentence, because all three show a
 * total before the bill exists.
 */
export const payable = (total) => Math.round(Number(total) || 0);

/** What rounding added (+) or took off (-). Zero when the total is whole. */
export const roundOffOf = (total) => {
  const v = Number(total) || 0;
  return +(Math.round(v) - v).toFixed(2);
};

/**
 * The shop's own setting, which a product follows unless it overrides it.
 *
 * Held here rather than on the organisation record because
 * PUT /organizations/:id is super-admin only - a shop sets this through its
 * own endpoint and reads it back from the same one.
 */
let orgTaxMode = 'exclusive';
export const shopTaxMode = () => orgTaxMode;
export const setShopTaxModeLocal = (mode) => {
  orgTaxMode = isInclusive(mode) ? 'inclusive' : 'exclusive';
};

/**
 * Turn tax-inclusive pricing on or off for the whole shop.
 *
 * Applied locally first so the switch answers the click, and put back if the
 * server refuses. This decides what every customer is charged, and a switch
 * that looks set but is not is the worst of the three states it could be in.
 */
export const saveShopTaxMode = async (orgId, mode) => {
  const before = orgTaxMode;
  setShopTaxModeLocal(mode);
  notify();
  try {
    const res = await fetchWithAuth('/org-settings/tax', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'x-org-id': orgId },
      body: JSON.stringify({ taxMode: orgTaxMode }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || res.statusText);
    }
    return { ok: true };
  } catch (e) {
    orgTaxMode = before;
    notify();
    return { ok: false, error: e.message };
  }
};

// ---- Product hierarchy CRUD ----
export const pCategoriesOf = (orgId) => pCategories.filter((c) => c.orgId === orgId).reverse();
export const pSubCategoriesOf = (orgId) => pSubCategories.filter((c) => c.orgId === orgId).reverse();
export const sCategoriesOf = (orgId) => sCategories.filter((c) => c.orgId === orgId).reverse();
export const sSubCategoriesOf = (orgId) => sSubCategories.filter((c) => c.orgId === orgId).reverse();

export const findPCategory = (id) => pCategories.find((c) => c.id === id) || null;
export const findPSubCategory = (id) => pSubCategories.find((c) => c.id === id) || null;

export const addPCategory = (record) => {
  const id = record.id || nextLocalId('pc');
  const catObj = { ...record, id };
  pCategories.push(catObj);

  fetchWithAuth('/product-categories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(catObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverCat => {
        catObj.id = serverCat.id;
        notify();
      });
    }
  });
  notify();
  return id;
};
export const updatePCategory = (id, patch) => {
  replaceIn(pCategories, id, patch);
  fetchWithAuth(`/product-categories/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  notify();
};
export const deletePCategory = (id) => {
  for (let i = pSubCategories.length - 1; i >= 0; i -= 1) {
    if (pSubCategories[i].categoryId === id) pSubCategories.splice(i, 1);
  }
  removeFrom(pCategories, id);
  fetchWithAuth(`/product-categories/${id}`, {
    method: 'DELETE',
  });
  notify();
};

export const addPSubCategory = (record) => {
  const id = record.id || nextLocalId('psc');
  const subCatObj = { ...record, id };
  pSubCategories.push(subCatObj);

  fetchWithAuth('/product-subcategories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subCatObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverSub => {
        subCatObj.id = serverSub.id;
        notify();
      });
    }
  });
  notify();
  return id;
};
export const updatePSubCategory = (id, patch) => {
  replaceIn(pSubCategories, id, patch);
  fetchWithAuth(`/product-subcategories/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  notify();
};
export const deletePSubCategory = (id) => {
  removeFrom(pSubCategories, id);
  fetchWithAuth(`/product-subcategories/${id}`, {
    method: 'DELETE',
  });
  notify();
};

// ---- Service hierarchy CRUD ----
export const findSCategory = (id) => sCategories.find((c) => c.id === id) || null;
export const findSSubCategory = (id) => sSubCategories.find((c) => c.id === id) || null;

export const addSCategory = (record) => {
  const id = record.id || nextLocalId('sc');
  const catObj = { ...record, id };
  sCategories.push(catObj);

  fetchWithAuth('/service-categories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(catObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverCat => {
        catObj.id = serverCat.id;
        notify();
      });
    }
  });
  notify();
  return id;
};
export const updateSCategory = (id, patch) => {
  replaceIn(sCategories, id, patch);
  fetchWithAuth(`/service-categories/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  notify();
};
export const deleteSCategory = (id) => {
  for (let i = sSubCategories.length - 1; i >= 0; i -= 1) {
    if (sSubCategories[i].categoryId === id) sSubCategories.splice(i, 1);
  }
  removeFrom(sCategories, id);
  fetchWithAuth(`/service-categories/${id}`, {
    method: 'DELETE',
  });
  notify();
};

export const addSSubCategory = (record) => {
  const id = record.id || nextLocalId('ssc');
  const subCatObj = { ...record, id };
  sSubCategories.push(subCatObj);

  fetchWithAuth('/service-subcategories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(subCatObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverSub => {
        subCatObj.id = serverSub.id;
        notify();
      });
    }
  });
  notify();
  return id;
};
export const updateSSubCategory = (id, patch) => {
  replaceIn(sSubCategories, id, patch);
  fetchWithAuth(`/service-subcategories/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  notify();
};
export const deleteSSubCategory = (id) => {
  removeFrom(sSubCategories, id);
  fetchWithAuth(`/service-subcategories/${id}`, {
    method: 'DELETE',
  });
  notify();
};

// ---- Master: Products (leaf of P-Category > P-Sub-Category > Product) ----
// A product carries variantFields (the matrix definition) + variants (the
// generated combinations). 21 combinations -> 21 variant rows.
export const catalogProducts = [];

// ---- Master: Services (leaf of S-Category > S-Sub-Category > Service) ----
export const catalogServices = [];

// Cartesian product of variant fields -> array of combo objects { label: value }.
export const buildVariantMatrix = (fields) => {
  const valid = (fields || [])
    .map((f) => ({
      label: (f.label || '').trim(),
      options: (f.options || []).map((o) => (o || '').trim()).filter(Boolean),
    }))
    .filter((f) => f.label && f.options.length);
  if (!valid.length) return [];
  let combos = [{}];
  valid.forEach((f) => {
    const next = [];
    combos.forEach((c) => { f.options.forEach((o) => next.push({ ...c, [f.label]: o })); });
    combos = next;
  });
  return combos;
};

export const comboLabel = (combo) => Object.values(combo || {}).join(' / ');
export const comboKey = (combo) => Object.entries(combo || {}).sort().map(([k, v]) => `${k}=${v}`).join('|');

// ---- Catalog Products / Services CRUD ----
export const catalogProductsOf = (orgId) => catalogProducts.filter((p) => p.orgId === orgId).reverse();
export const catalogServicesOf = (orgId) => catalogServices.filter((s) => s.orgId === orgId).reverse();
export const findCatalogProduct = (id) => catalogProducts.find((p) => p.id === id) || null;
export const findCatalogService = (id) => catalogServices.find((s) => s.id === id) || null;

export const addCatalogProduct = async (record) => {
  const id = record.id || nextLocalId('cp');
  const prodObj = { ...record, id };
  catalogProducts.push(prodObj);
  notify();

  try {
    const res = await fetchWithAuth('/products', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(prodObj),
    });
    if (res.ok) {
      const serverProd = await res.json();
      const oldId = prodObj.id;
      prodObj.id = serverProd.id;
      stockRows.forEach(sr => {
        if (sr.productId === oldId) sr.productId = serverProd.id;
      });
      notify();
      return serverProd.id;
    }
  } catch (e) {
    console.error('Failed to create catalog product:', e);
  }
  return id;
};
export const updateCatalogProduct = async (id, patch) => {
  replaceIn(catalogProducts, id, patch);
  notify();
  try {
    const res = await fetchWithAuth(`/products/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(errData.error || 'Failed to update catalog product');
    }
  } catch (e) {
    console.error('Failed to update catalog product:', e);
    throw e;
  }
};
export const deleteCatalogProduct = (id) => {
  removeFrom(catalogProducts, id);
  fetchWithAuth(`/products/${id}`, {
    method: 'DELETE',
  });
  notify();
};

export const addCatalogService = (record) => {
  const id = record.id || nextLocalId('cs');
  const srvObj = { ...record, id };
  catalogServices.push(srvObj);

  fetchWithAuth('/services', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(srvObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverSrv => {
        srvObj.id = serverSrv.id;
        notify();
      });
    }
  });
  notify();
  return id;
};
export const updateCatalogService = (id, patch) => {
  replaceIn(catalogServices, id, patch);
  fetchWithAuth(`/services/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  notify();
};
export const deleteCatalogService = (id) => {
  removeFrom(catalogServices, id);
  fetchWithAuth(`/services/${id}`, {
    method: 'DELETE',
  });
  notify();
};

// ---- Branch / warehouse-aware stock & purchasing ----
// A "stock location" is either a branch id or the org's central warehouse.
export const CENTRAL_LOCATION = 'central';

// Resolves how Purchases and Inventory are scoped. With the zone-aware model
// branches always own their own inventory; warehouses handle their own
// purchasing separately. So purchases and inventory are always per-branch
// from the branch-tab perspective.
export const resolveStockScopes = (orgId) => {
  const s = getOrgSettings(orgId);
  return {
    branchesEnabled: !!s.branchesEnabled,
    warehouseEnabled: !!s.warehouseEnabled,
    mode: 0,
    purchaseScope: 'branch',
    inventoryScope: 'branch',
  };
};

// Stock rows: per branch, per product variant. status pending | active.
export const stockRows = [];

// Purchase orders: raised at a location, seed a pending stock row.
export const purchaseOrders = [];

export const stockRowsOf = (orgId, locationId, status = null) => stockRows.filter((r) =>
  r.orgId === orgId && (locationId ? r.locationId === locationId : true) && (status ? r.status === status : true) && r.note !== 'T0 Stock'
).reverse();

export const stockHistoryOf = (orgId, locationId) => stockHistory.filter((r) =>
  r.orgId === orgId && (locationId ? r.locationId === locationId : true)
).reverse();

export const purchaseOrdersOf = (orgId, locationId) => purchaseOrders.filter((p) =>
  p.orgId === orgId && (locationId ? p.locationId === locationId : true)).reverse();

export const inventoryLocationFor = (orgId, purchaseLocationId) => {
  const { inventoryScope } = resolveStockScopes(orgId);
  return inventoryScope === 'central' ? CENTRAL_LOCATION : purchaseLocationId;
};

export const refreshStock = async () => {
  try {
    const res = await fetchWithAuth('/stock');
    if (res.ok) {
      const data = await res.json();
      stockRows.length = 0;
      stockRows.push(...data.filter(r => r.note !== 'T0 Stock'));
      notify();
    }
  } catch(e) { console.error('refreshStock failed', e); }
};

export const addPurchaseOrder = (record) => {
  const id = record.id || nextLocalId('po');
  const po = { ...record, id };
  purchaseOrders.push(po);
  
  if (po.status === 'Received' && po.productId) {
    stockRows.push({
      id: nextLocalId('stk'),
      orgId: po.orgId,
      locationId: po.locationId || 'main',
      productId: po.productId,
      variantId: po.variantId || '',
      qty: Number(po.qty) || 0,
      costPrice: Number(po.costPrice) || 0,
      batchNumber: po.batchNumber || '',
      expiryDate: po.expiryDate || '',
      barcode: po.barcode || '',
      status: 'pending',
      updatedAt: new Date().toISOString()
    });
    notify();
  }

  fetchWithAuth('/purchase-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(po),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverPo => {
        po.id = serverPo.id;
        refreshStock();
      });
    }
  });

  return id;
};

export const updatePurchaseOrder = (id, patch) => {
  replaceIn(purchaseOrders, id, patch);
  const po = purchaseOrders.find(p => p.id === id);
  if (po) {
    fetchWithAuth(`/purchase-orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(po),
    }).then(res => {
      if (res.ok) refreshStock();
    });
  }
};
export const deletePurchaseOrder = (id) => {
  removeFrom(purchaseOrders, id);
  fetchWithAuth(`/purchase-orders/${id}`, {
    method: 'DELETE',
  });
};

export const commitStockRow = (id, sellPrice, minStockThreshold = 0, batchNumber = undefined, expiryDate = undefined, barcode = undefined) => {
  const isoTime = new Date().toISOString();
  const row = stockRows.find(r => r.id === id);
  if (!row) return;

  const finalBatch = batchNumber !== undefined ? batchNumber : (row.batchNumber || '');
  const finalExpiry = expiryDate !== undefined ? expiryDate : (row.expiryDate || '');
  const finalBarcode = barcode !== undefined ? barcode : (row.barcode || '');

  const existingActive = stockRows.find(r => 
    r.status === 'active' &&
    r.orgId === row.orgId &&
    r.locationId === row.locationId &&
    r.productId === row.productId &&
    (r.variantId || '') === (row.variantId || '')
  );

  if (existingActive) {
    updateStockRow(existingActive.id, {
      qty: Number(existingActive.qty) + Number(row.qty),
      sellPrice: Number(sellPrice) || 0,
      minStockThreshold: Number(minStockThreshold) || 0,
      batchNumber: finalBatch || existingActive.batchNumber || '',
      expiryDate: finalExpiry || existingActive.expiryDate || '',
      barcode: finalBarcode || existingActive.barcode || '',
    });
    stockHistory.push({
      id: nextLocalId('hist'),
      orgId: row.orgId,
      locationId: row.locationId,
      productId: row.productId,
      variantId: row.variantId,
      qty: row.qty,
      reason: row.note || 'Merged to Active',
      createdAt: isoTime
    });
    deleteStockRow(id);
  } else {
    if (row.status === 'pending') {
      stockHistory.push({
        id: nextLocalId('hist'),
        orgId: row.orgId,
        locationId: row.locationId,
        productId: row.productId,
        variantId: row.variantId,
        qty: row.qty,
        reason: row.note || 'Moved to Active',
        createdAt: isoTime
      });
    }
    updateStockRow(id, {
      sellPrice: Number(sellPrice) || 0,
      minStockThreshold: Number(minStockThreshold) || 0,
      batchNumber: finalBatch,
      expiryDate: finalExpiry,
      barcode: finalBarcode || row.barcode || '',
      status: 'active'
    });
  }
};
export const updateStockRow = async (id, patch) => {
  const isoTime = new Date().toISOString();
  const nextPatch = { ...patch, updatedAt: isoTime };
  replaceIn(stockRows, id, nextPatch);
  notify();
  const row = stockRows.find(r => r.id === id);
  if (row) {
    const { product, ...payload } = row;
    payload.updatedAt = isoTime;
    try {
      const res = await fetchWithAuth(`/stock/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 404) {
        const res2 = await fetchWithAuth('/stock', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (res2.ok) {
          const serverSt = await res2.json();
          row.id = serverSt.id;
          notify();
        }
      }
    } catch (e) {
      console.error('Failed to update stock row:', e);
    }
  }
};
export const deleteStockRow = (id) => {
  removeFrom(stockRows, id);
  notify();
  fetchWithAuth(`/stock/${id}`, {
    method: 'DELETE',
  });
};
export const addStockRow = async (record) => {
  const id = nextLocalId('st');
  const isoTime = new Date().toISOString();
  const rowObj = {
    id,
    status: 'active',
    updatedAt: isoTime,
    ...record,
  };
  stockRows.push(rowObj);
  // Record history only for Active→Pending transfers (which carry a reason note).
  // Manual pending adds have no note so they are silently skipped.
  // We also skip logging incoming transfers since they are not deductions from active stock.
  if (rowObj.status === 'pending' && rowObj.note && !rowObj.isTransferReceipt) {
    stockHistory.push({
      id: nextLocalId('hist'),
      orgId: rowObj.orgId,
      locationId: rowObj.locationId,
      productId: rowObj.productId,
      variantId: rowObj.variantId,
      qty: rowObj.qty,
      reason: rowObj.note,
      createdAt: new Date().toISOString()
    });
  }
  notify();

  const { product, ...payload } = rowObj;
  try {
    const res = await fetchWithAuth('/stock', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) {
      const serverSt = await res.json();
      rowObj.id = serverSt.id;
      notify();
      return serverSt.id;
    }
  } catch (e) {
    console.error('Failed to add stock row:', e);
  }
  return id;
};

// Resolves a readable variant label for a product + variantId.
export const variantLabelOf = (product, variantId) => {
  if (!product) return '';
  if (!variantId) return 'Standard';
  const v = (product.variants || []).find((x) => x.id === variantId);
  return v ? v.label : 'Standard';
};

// Resolves which images to show for a product + variantId, honoring the
// product's imageMode: 'common' (shared set), 'variant' (per-variant set), or
// 'both' (variant images followed by the shared set). Falls back to inferring
// the mode for products saved before imageMode existed.
export const productImagesOf = (product, variantId) => {
  if (!product) return [];
  const common = product.images || [];
  const variant = (product.variants || []).find((x) => x.id === variantId)?.images || [];
  const mode = product.imageMode
    || (variant.length ? (common.length ? 'both' : 'variant') : 'common');
  if (mode === 'variant') return variant.length ? variant : common;
  if (mode === 'both') return [...variant, ...common];
  return common;
};

// ---- Delivery & fulfillment ----
// A delivery is a customer order shipped from a branch. It moves through a
// fixed stage pipeline and can be handed to a delivery agent for that branch.
export const DELIVERY_STAGES = ['new', 'preparing', 'dispatched', 'delivered'];

export const DELIVERY_STAGE_META = {
  new:        { label: 'New',              hint: 'Awaiting pick & pack', color: '#0369a1' },
  preparing:  { label: 'Preparing',        hint: 'Being packed',         color: '#b45309' },
  dispatched: { label: 'Out for delivery', hint: 'On the way',           color: '#7c3aed' },
  delivered:  { label: 'Delivered',        hint: 'Handed to customer',   color: '#0a7d3c' },
  cancelled:  { label: 'Cancelled',        hint: 'Order cancelled',      color: '#be123c' },
};

// Delivery agents belong to a single branch and carry an availability flag.
export const deliveryAgents = [];

export const deliveries = [];

// Deliveries for an org, optionally narrowed to one branch.
export const deliveriesOf = (orgId, locationId = null) =>
  deliveries.filter((d) => d.orgId === orgId && (locationId ? d.locationId === locationId : true)).reverse();
// Agents for an org, optionally narrowed to one branch.
export const deliveryAgentsOf = (orgId, locationId = null) =>
  deliveryAgents.filter((a) => a.orgId === orgId && (locationId ? a.branchId === locationId : true)).reverse();
export const findDelivery = (id) => deliveries.find((d) => d.id === id) || null;
export const findDeliveryAgent = (id) => deliveryAgents.find((a) => a.id === id) || null;

export const addDelivery = (record) => {
  const id = record.id || nextLocalId('dl');
  const delObj = { ...record, id };
  deliveries.push(delObj);

  fetchWithAuth('/deliveries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(delObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverDel => {
        delObj.id = serverDel.id;
      });
    }
  });
  return id;
};
export const updateDelivery = (id, patch) => {
  replaceIn(deliveries, id, patch);
  const del = deliveries.find(d => d.id === id);
  if (del) {
    fetchWithAuth(`/deliveries/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(del),
    });
  }
};
export const deleteDelivery = (id) => {
  removeFrom(deliveries, id);
  fetchWithAuth(`/deliveries/${id}`, {
    method: 'DELETE',
  });
};

// The stage that follows `status`, or null when already delivered / off-pipeline.
export const nextDeliveryStage = (status) => {
  const i = DELIVERY_STAGES.indexOf(status);
  return i >= 0 && i < DELIVERY_STAGES.length - 1 ? DELIVERY_STAGES[i + 1] : null;
};
export const advanceDelivery = (id) => {
  const d = deliveries.find((x) => x.id === id);
  if (!d) return;
  const next = nextDeliveryStage(d.status);
  if (next) {
    replaceIn(deliveries, id, { status: next });
    const updated = deliveries.find((x) => x.id === id);
    fetchWithAuth(`/deliveries/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    });
  }
};

export const addDeliveryAgent = (record) => {
  const id = record.id || nextLocalId('da');
  const agentObj = { ...record, id, status: record.status || 'available' };
  deliveryAgents.push(agentObj);

  fetchWithAuth('/delivery-agents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(agentObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverAgent => {
        agentObj.id = serverAgent.id;
      });
    }
  });
  return id;
};
export const updateDeliveryAgent = (id, patch) => {
  replaceIn(deliveryAgents, id, patch);
  const agent = deliveryAgents.find(a => a.id === id);
  if (agent) {
    fetchWithAuth(`/delivery-agents/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(agent),
    });
  }
};
export const deleteDeliveryAgent = (id) => {
  removeFrom(deliveryAgents, id);
  fetchWithAuth(`/delivery-agents/${id}`, {
    method: 'DELETE',
  });
};

// ---- Transport / stock transfer ----
// A transfer moves goods between two stock locations (branches / central
// warehouse). It progresses through a fixed pipeline and can carry a carrier.
export const TRANSFER_STAGES = ['requested', 'approved', 'transit', 'received'];

export const TRANSFER_STAGE_META = {
  requested: { label: 'Requested',  hint: 'Awaiting approval',   color: '#0369a1' },
  approved:  { label: 'Approved',   hint: 'Ready to dispatch',   color: '#b45309' },
  transit:   { label: 'In Transit', hint: 'On the move',         color: '#7c3aed' },
  received:  { label: 'Received',   hint: 'Arrived & stocked',   color: '#0a7d3c' },
  cancelled: { label: 'Cancelled',  hint: 'Transfer cancelled',  color: '#be123c' },
};

// The stock locations a transfer can move goods between: every effective
// branch plus every real warehouse the org has provisioned.
export const stockLocationsOf = (orgId) => {
  const list = effectiveBranchesOf(orgId).map((b) => ({ id: b.id, name: b.name, kind: 'branch' }));
  if (getOrgSettings(orgId).warehouseEnabled) {
    warehousesOf(orgId).forEach((w) => list.unshift({ id: w.id, name: w.name, kind: 'warehouse' }));
  }
  return list;
};
export const locationLabelOf = (id) => {
  if (id === CENTRAL_LOCATION) return 'Central Warehouse';
  const wh = warehouses.find((w) => w.id === id);
  if (wh) return wh.name;
  return findBranch(id)?.name || id;
};

export const stockTransfers = [];

export const stockTransfersOf = (orgId) => stockTransfers.filter((t) => t.orgId === orgId).reverse();
export const findStockTransfer = (id) => stockTransfers.find((t) => t.id === id) || null;

// True when a stock location id points at a real warehouse (not a branch).
export const isWarehouseLocation = (locationId) => !!warehouses.find((w) => w.id === locationId);

export const addStockTransfer = (record) => {
  const id = record.id || nextLocalId('tr');
  const trObj = { ...record, id };
  stockTransfers.push(trObj);

  fetchWithAuth('/stock-transfers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(trObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverTr => {
        trObj.id = serverTr.id;
      });
    }
  });
  return id;
};
export const updateStockTransfer = (id, patch) => {
  const t = stockTransfers.find((x) => x.id === id);
  if (!t) return;
  
  if (patch.status === 'received' && !t.stockApplied) {
    applyTransferToInventory(t);
    patch.stockApplied = true;

    if (t.note) {
      const match = t.note.match(/\[ReqID:(.+?)\]/);
      if (match && match[1]) {
        const req = findEmergencyRequest(match[1]);
        if (req && req.status === 'fulfilled') {
          updateEmergencyRequestStatus(req.id, 'completed');
        }
      }
    }
  }

  replaceIn(stockTransfers, id, patch);
  const updated = stockTransfers.find((x) => x.id === id);
  fetchWithAuth(`/stock-transfers/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updated),
  });
};
export const deleteStockTransfer = (id) => {
  removeFrom(stockTransfers, id);
  fetchWithAuth(`/stock-transfers/${id}`, {
    method: 'DELETE',
  });
};

// The stage that follows `status`, or null when already received / off-pipeline.
export const nextTransferStage = (status) => {
  const i = TRANSFER_STAGES.indexOf(status);
  return i >= 0 && i < TRANSFER_STAGES.length - 1 ? TRANSFER_STAGES[i + 1] : null;
};
// Resolve a transfer line to a catalog product + variant id. Prefers the ids
// stored on the line; falls back to matching by product name + variant label
// (for older transfers saved before line ids existed).
function resolveTransferLineRef(orgId, line) {
  if (line.productId) return { productId: line.productId, variantId: line.variantId || '' };
  const product = catalogProductsOf(orgId).find((p) => p.name === line.name);
  if (!product) return null;
  let variantId = '';
  if (line.label) {
    const v = (product.variants || []).find((x) => x.label === line.label);
    variantId = v ? v.id : '';
  }
  return { productId: product.id, variantId };
};

// When a transfer is received, land each line in the destination location's
// PENDING inventory (mirrors how a purchase seeds a pending row). The branch
// then sets a sell price to commit it to active stock.
function applyTransferToInventory(t) {
  (t.lines || []).forEach((line) => {
    const ref = resolveTransferLineRef(t.orgId, line);
    if (!ref) return; // can't map to a catalog product — skip rather than guess
    
    // Find active stock in the source location to determine an average cost price
    const sourceRows = stockRows.filter(r => 
      r.orgId === t.orgId && 
      r.locationId === t.fromLocationId && 
      r.productId === ref.productId && 
      r.variantId === ref.variantId && 
      r.status === 'active'
    );
    
    let totalQty = 0;
    let totalCost = 0;
    sourceRows.forEach(r => {
      totalQty += r.qty;
      totalCost += r.qty * (Number(r.costPrice) || 0);
    });
    
    let resolvedCost = 0;
    if (totalQty > 0) {
      resolvedCost = totalCost / totalQty;
    } else {
      // Fallback: Check if there is any stock of this product anywhere in the org to guess a cost
      const anyRows = stockRows.filter(r => 
        r.orgId === t.orgId && 
        r.productId === ref.productId && 
        r.variantId === ref.variantId && 
        Number(r.costPrice) > 0
      );
      if (anyRows.length > 0) {
        resolvedCost = Number(anyRows[0].costPrice) || 0;
      }
    }

    const sourceRow = sourceRows[0] || null;
    addStockRow({
      orgId: t.orgId,
      locationId: t.toLocationId,
      productId: ref.productId,
      variantId: ref.variantId,
      qty: Number(line.qty) || 0,
      costPrice: resolvedCost,
      sellPrice: 0,
      batchNumber: line.batchNumber || sourceRow?.batchNumber || '',
      expiryDate: line.expiryDate || sourceRow?.expiryDate || '',
      barcode: line.barcode || sourceRow?.barcode || '',
      status: 'pending',
      note: t.note && (t.note.toLowerCase().includes('emerg') || t.note.toLowerCase().includes('emery'))
            ? `From ${locationLabelOf(t.fromLocationId)} (Emergency)` 
            : `From ${locationLabelOf(t.fromLocationId)}`,
      isTransferReceipt: true,
    });
  });
};

export const advanceTransfer = (id) => {
  const t = stockTransfers.find((x) => x.id === id);
  if (!t) return;
  const next = nextTransferStage(t.status);
  if (!next) return;
  updateStockTransfer(id, { status: next });
};

// ---- Emergency Requests ----
export const emergencyRequests = [];
export const emergencyRequestsOf = (orgId) => emergencyRequests.filter((r) => r.orgId === orgId).reverse();
export const findEmergencyRequest = (id) => emergencyRequests.find((r) => r.id === id) || null;

export const addEmergencyRequest = (record) => {
  const id = record.id || nextLocalId('er');
  const reqObj = { ...record, id };
  emergencyRequests.push(reqObj);

  fetchWithAuth('/emergency-requests', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reqObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverReq => {
        reqObj.id = serverReq.id;
      });
    }
  });
  return id;
};

export const updateEmergencyRequestStatus = (id, newStatus) => {
  const req = emergencyRequests.find(r => r.id === id);
  if (!req) return;
  replaceIn(emergencyRequests, id, { status: newStatus });
  fetchWithAuth(`/emergency-requests/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...req, status: newStatus }),
  });
  notify();
};

export const fulfillEmergencyRequest = (id, fulfillments) => {
  const req = emergencyRequests.find(r => r.id === id);
  if (!req) return;
  
  let newFulfilledQty = (req.fulfilledQty || 0);
  fulfillments.forEach(f => { newFulfilledQty += Number(f.qty); });

  const patch = { fulfilledQty: newFulfilledQty };
  if (newFulfilledQty >= req.qty) {
    patch.status = 'fulfilled';
  }
  
  replaceIn(emergencyRequests, id, patch);
  
  // Update backend
  fetchWithAuth(`/emergency-requests/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...req, ...patch }),
  });

  // Automatically create a standard transfer to move the stock for each location
  const product = catalogProducts.find(p => p.id === req.productId);
  const variant = (product?.variants || []).find(v => v.id === req.variantId);
  
  fulfillments.forEach(f => {
    if (f.qty > 0) {
      // --- Deduct Stock from source location ---
      let qtyToDeduct = Number(f.qty);

      // 1. Deduct from pending reserved stock
      const pendingRows = stockRows.filter(r => {
        return r.orgId === req.orgId && r.locationId === f.locationId && r.productId === req.productId && r.variantId === (req.variantId || '') && r.status === 'pending';
      });
      for (const pRow of pendingRows) {
        if (qtyToDeduct <= 0) break;
        const deduct = Math.min(pRow.qty, qtyToDeduct);
        if (deduct > 0) {
          if (pRow.qty - deduct <= 0) {
            deleteStockRow(pRow.id);
          } else {
            updateStockRow(pRow.id, { qty: pRow.qty - deduct });
          }
          qtyToDeduct -= deduct;
        }
      }

      // 2. If there's still qty to deduct, deduct from active stock
      if (qtyToDeduct > 0) {
        const activeRows = stockRows.filter(r => r.orgId === req.orgId && r.locationId === f.locationId && r.productId === req.productId && r.variantId === (req.variantId || '') && r.status === 'active');
        for (const aRow of activeRows) {
          if (qtyToDeduct <= 0) break;
          const deduct = Math.min(aRow.qty, qtyToDeduct);
          if (deduct > 0) {
            updateStockRow(aRow.id, { qty: aRow.qty - deduct });
            qtyToDeduct -= deduct;
          }
        }
      }

      // --- Create or Update Stock Transfer ---
      const existing = stockTransfers.find(t => 
        t.orgId === req.orgId && 
        t.fromLocationId === f.locationId && 
        t.toLocationId === req.requestingBranchId &&
        t.status === 'requested' &&
        t.note && t.note.includes('Emergency request fulfillment')
      );

      const newLine = {
        productId: req.productId,
        variantId: req.variantId || '',
        name: product ? product.name : 'Unknown',
        label: variant ? variant.label : '',
        qty: Number(f.qty)
      };

      if (existing) {
        const updatedLines = [...(existing.lines || []), newLine];
        const updatedNote = existing.note.includes(`[ReqID:${req.id}]`) 
          ? existing.note 
          : `${existing.note}, [ReqID:${req.id}]`;
        updateStockTransfer(existing.id, { lines: updatedLines, note: updatedNote });
      } else {
        addStockTransfer({
          orgId: req.orgId,
          fromLocationId: f.locationId,
          toLocationId: req.requestingBranchId,
          carrier: '',
          vehicle: 'Van',
          note: `Emergency request fulfillment [ReqID:${req.id}] for ${locationLabelOf(req.requestingBranchId)}`,
          status: 'requested',
          createdAt: new Date().toISOString().slice(0,10),
          lines: [newLine]
        });
      }
    }
  });
};

export const deleteEmergencyRequest = (id) => {
  removeFrom(emergencyRequests, id);
  fetchWithAuth(`/emergency-requests/${id}`, { method: 'DELETE' });
};


// ---- E-commerce: storefront ----
// Per-org online storefront configuration. status 'live' | 'paused'.
export const storefrontSettings = [];

export const getStorefront = (orgId) => {
  let s = storefrontSettings.find((x) => x.orgId === orgId);
  if (!s) {
    const org = findOrgById(orgId);
    s = { orgId, storeName: org?.name || 'My Store', tagline: 'Shop online with us.', status: 'paused', delivery: true, pickup: true };
    storefrontSettings.push(s);
  }
  return s;
};
export const updateStorefront = (orgId, patch) => {
  const s = getStorefront(orgId);
  Object.assign(s, patch);
  fetchWithAuth('/storefront-settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(s),
  });
  return s;
};

// Per-product online listing. Catalog products with no record default unpublished.
export const storeListings = [];

export const storeListingsOf = (orgId) => storeListings.filter((l) => l.orgId === orgId);
export const listingFor = (orgId, productId) =>
  storeListings.find((l) => l.orgId === orgId && l.productId === productId) || null;
// Upsert the storefront listing for a product.
export const setListing = (orgId, productId, patch) => {
  let l = storeListings.find((x) => x.orgId === orgId && x.productId === productId);
  if (!l) {
    l = { id: nextLocalId('sl'), orgId, productId, published: false, onlinePrice: 0 };
    storeListings.push(l);
  }
  Object.assign(l, patch);

  fetchWithAuth('/store-listings', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(l),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverListing => {
        l.id = serverListing.id;
      });
    }
  });
  return l;
};

// ---- E-commerce: online orders ----
export const ONLINE_ORDER_STAGES = ['placed', 'confirmed', 'packed', 'completed'];
export const ONLINE_ORDER_STAGE_META = {
  placed:    { label: 'New Order', hint: 'Awaiting confirmation', color: '#0369a1' },
  confirmed: { label: 'Confirmed', hint: 'Accepted, to pack',     color: '#b45309' },
  packed:    { label: 'Packed',    hint: 'Ready to hand over',    color: '#7c3aed' },
  completed: { label: 'Completed', hint: 'Order fulfilled',       color: '#0a7d3c' },
  cancelled: { label: 'Cancelled', hint: 'Order cancelled',       color: '#be123c' },
};

export const onlineOrders = [];

export const onlineOrdersOf = (orgId) => onlineOrders.filter((o) => o.orgId === orgId);
export const findOnlineOrder = (id) => onlineOrders.find((o) => o.id === id) || null;

export const addOnlineOrder = (record) => {
  const id = record.id || nextLocalId('ord');
  const ordObj = { ...record, id };
  onlineOrders.push(ordObj);

  fetchWithAuth('/online-orders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(ordObj),
  }).then(res => {
    if (res.ok) {
      res.json().then(serverOrd => {
        ordObj.id = serverOrd.id;
      });
    }
  });
  return id;
};
export const updateOnlineOrder = (id, patch) => {
  replaceIn(onlineOrders, id, patch);
  const ord = onlineOrders.find(o => o.id === id);
  if (ord) {
    fetchWithAuth(`/online-orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ord),
    });
  }
};

export const deleteOnlineOrder = (id) => {
  removeFrom(onlineOrders, id);
  fetchWithAuth(`/online-orders/${id}`, {
    method: 'DELETE',
  });
};

// The stage that follows `status`, or null when already completed / off-pipeline.
export const nextOrderStage = (status) => {
  const i = ONLINE_ORDER_STAGES.indexOf(status);
  return i >= 0 && i < ONLINE_ORDER_STAGES.length - 1 ? ONLINE_ORDER_STAGES[i + 1] : null;
};
export const advanceOnlineOrder = (id) => {
  const o = onlineOrders.find((x) => x.id === id);
  if (!o) return;
  const next = nextOrderStage(o.status);
  if (next) {
    replaceIn(onlineOrders, id, { status: next });
    const updated = onlineOrders.find((x) => x.id === id);
    fetchWithAuth(`/online-orders/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updated),
    });
  }
};

// ---- E-commerce: Delivery Fees (distance-based) ----
export const deliveryFees = [];

export const deliveryFeesOf = (orgId) => deliveryFees.filter((f) => f.orgId === orgId);

export const addDeliveryFee = (record) => {
  const tempId = nextLocalId('df');
  const obj = { ...record, id: tempId };
  deliveryFees.push(obj);
  return fetchWithAuth('/delivery-fees', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(obj),
  }).then(async (res) => {
    if (res.ok) {
      const server = await res.json();
      obj.id = server.id;
      notify();
    }
  });
};

export const updateDeliveryFee = (id, patch) => {
  replaceIn(deliveryFees, id, patch);
  const item = deliveryFees.find((f) => f.id === id);
  if (item) {
    fetchWithAuth(`/delivery-fees/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });
  }
};

export const deleteDeliveryFee = (id) => {
  removeFrom(deliveryFees, id);
  fetchWithAuth(`/delivery-fees/${id}`, { method: 'DELETE' });
};

// Demo credentials shown on the login screens (so reviewers can sign in).
export const DEMO_CREDENTIALS = [];


// ---- Service Modules CRUD ----
export const addCustomerFeedback = (record) => {
  const id = record.id || nextLocalId('fb');
  const fbObj = { ...record, id };
  customerFeedback.push(fbObj);
  notify();
  return fetchWithAuth('/feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(fbObj),
  });
};

export const addAppointment = async (record) => {
  const res = await fetchWithAuth('/appointments', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(record),
  });
  if (res.ok) {
    const data = await res.json();
    appointments.push(data);
    notify();
    return data;
  }
  throw new Error('Failed to add appointment');
};

export const updateAppointment = async (id, patch) => {
  const res = await fetchWithAuth(`/appointments/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (res.ok) {
    const data = await res.json();
    replaceIn(appointments, id, data);
    notify();
    return data;
  }
  throw new Error('Failed to update appointment');
};

export const deleteAppointment = async (id) => {
  const res = await fetchWithAuth(`/appointments/${id}`, {
    method: 'DELETE',
  });
  if (res.ok) {
    removeFrom(appointments, id);
    notify();
    return true;
  }
  throw new Error('Failed to delete appointment');
};

export const addMembership = (record) => {
  const id = record.id || nextLocalId('mem');
  const memObj = { ...record, id };
  memberships.push(memObj);
  notify();
  return fetchWithAuth('/membershipplans', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' },
    body: JSON.stringify(memObj),
  });
};

export const updateMembership = (id, patch) => {
  replaceIn(memberships, id, patch);
  notify();
  return fetchWithAuth(`/membershipplans/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' },
    body: JSON.stringify(patch),
  });
};

export const deleteMembership = (id) => {
  removeFrom(memberships, id);
  notify();
  return fetchWithAuth(`/membershipplans/${id}`, {
    method: 'DELETE',
    headers: { 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' }
  });
};

export const addCustomerMembership = (record) => {
  const id = record.id || nextLocalId('cm');
  const cmObj = { ...record, id };
  customerMemberships.push(cmObj);
  notify();
  return fetchWithAuth('/customermemberships', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' },
    body: JSON.stringify(cmObj),
  });
};

export const updateCustomerMembership = (id, patch) => {
  replaceIn(customerMemberships, id, patch);
  notify();
  return fetchWithAuth(`/customermemberships/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' },
    body: JSON.stringify(patch),
  });
};

export const deleteCustomerMembership = (id) => {
  removeFrom(customerMemberships, id);
  notify();
  return fetchWithAuth(`/customermemberships/${id}`, {
    method: 'DELETE',
    headers: { 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' }
  });
};

// ---- Shifts & Schedules ----
export let shifts = [];
export let weeklySchedules = [];

export const addShift = (record) => {
  const id = record.id || nextLocalId('shf');
  const shfObj = { ...record, id };
  shifts.push(shfObj);
  notify();
  return fetchWithAuth('/staff-shifts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' },
    body: JSON.stringify(shfObj),
  });
};

export const updateShift = (id, patch) => {
  replaceIn(shifts, id, patch);
  notify();
  return fetchWithAuth(`/staff-shifts/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' },
    body: JSON.stringify(patch),
  });
};

export const deleteShift = (id) => {
  removeFrom(shifts, id);
  notify();
  return fetchWithAuth(`/staff-shifts/${id}`, {
    method: 'DELETE',
    headers: { 'x-org-id': localStorage.getItem('org.org') ? JSON.parse(localStorage.getItem('org.org')).id : '' }
  });
};

export const updateWeeklySchedule = (orgId, userId, weekStartDate, schedulePatch) => {
  let record = weeklySchedules.find(s => s.orgId === orgId && s.userId === userId && s.weekStartDate === weekStartDate);
  if (!record) {
    record = { id: nextLocalId('sch'), orgId, userId, weekStartDate, schedule: schedulePatch };
    weeklySchedules.push(record);
  } else {
    record.schedule = { ...record.schedule, ...schedulePatch };
  }
  notify();
};

export const updateOrganization = (id, patch) => {
  replaceIn(organizations, id, patch);
  notify();
};
